export const POPOVER_VIEWPORT_PADDING_PX = 16;
export const POPOVER_DEFAULT_OFFSET_PX = 8;

export type PopoverFitAlign = "start" | "end";

const POPOVER_FIT_POSITION_EPSILON_PX = 1;

type ViewportBox = {
	top: number;
	bottom: number;
	left: number;
	right: number;
};

function getViewportBox(): ViewportBox {
	const viewportTop = window.visualViewport?.offsetTop ?? 0;
	const viewportLeft = window.visualViewport?.offsetLeft ?? 0;
	const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
	const viewportWidth = window.visualViewport?.width ?? window.innerWidth;

	return {
		top: viewportTop + POPOVER_VIEWPORT_PADDING_PX,
		bottom: viewportTop + viewportHeight - POPOVER_VIEWPORT_PADDING_PX,
		left: viewportLeft + POPOVER_VIEWPORT_PADDING_PX,
		right: viewportLeft + viewportWidth - POPOVER_VIEWPORT_PADDING_PX,
	};
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), Math.max(min, max));
}

function getHorizontalLeft(
	triggerRect: DOMRect,
	popoverWidth: number,
	viewport: ViewportBox,
	align: PopoverFitAlign
): number {
	const alignedLeft =
		align === "end" ? triggerRect.right - popoverWidth : triggerRect.left;

	return clamp(alignedLeft, viewport.left, viewport.right - popoverWidth);
}

function getVerticalTop(
	triggerRect: DOMRect,
	popoverHeight: number,
	viewport: ViewportBox,
	offsetPx: number
): number {
	const spaceBelow = viewport.bottom - triggerRect.bottom - offsetPx;
	const spaceAbove = triggerRect.top - viewport.top - offsetPx;

	if (spaceBelow >= popoverHeight) {
		return triggerRect.bottom + offsetPx;
	}

	if (spaceAbove >= popoverHeight) {
		return triggerRect.top - offsetPx - popoverHeight;
	}

	return clamp(
		triggerRect.bottom + offsetPx,
		viewport.top,
		viewport.bottom - popoverHeight
	);
}

function applyFixedPosition(
	element: HTMLElement,
	nextTop: number,
	nextLeft: number,
	currentRect: DOMRect
): void {
	if (
		Math.abs(nextTop - currentRect.top) < POPOVER_FIT_POSITION_EPSILON_PX &&
		Math.abs(nextLeft - currentRect.left) < POPOVER_FIT_POSITION_EPSILON_PX &&
		element.style.position === "fixed"
	) {
		return;
	}

	element.style.setProperty("position", "fixed", "important");
	element.style.setProperty("top", `${nextTop}px`, "important");
	element.style.setProperty("left", `${nextLeft}px`, "important");
	element.style.setProperty("right", "auto", "important");
	element.style.setProperty("bottom", "auto", "important");
	element.style.setProperty("margin", "0", "important");
	element.style.setProperty("transform", "none", "important");
}

/**
 * React Aria can place the popover with `bottom` relative to `document.body`,
 * which detaches it from the trigger. Reposition from the trigger: keep it
 * adjacent when there is room, and pin it in the viewport only when neither
 * side fits the full calendar.
 */
export function fitPopoverInViewport(
	element: HTMLElement,
	trigger: HTMLElement | null,
	offsetPx = POPOVER_DEFAULT_OFFSET_PX,
	align: PopoverFitAlign = "start"
): void {
	if (element.style.maxHeight !== "none") {
		element.style.setProperty("max-height", "none", "important");
	}

	if (element.style.overflow !== "visible") {
		element.style.setProperty("overflow", "visible", "important");
	}

	const popoverRect = element.getBoundingClientRect();
	if (popoverRect.height < 1 || popoverRect.width < 1) {
		return;
	}

	const viewport = getViewportBox();

	if (!trigger) {
		const nextTop = clamp(
			popoverRect.top,
			viewport.top,
			viewport.bottom - popoverRect.height
		);
		applyFixedPosition(element, nextTop, popoverRect.left, popoverRect);
		return;
	}

	const triggerRect = trigger.getBoundingClientRect();
	const nextTop = getVerticalTop(
		triggerRect,
		popoverRect.height,
		viewport,
		offsetPx
	);
	const nextLeft = getHorizontalLeft(
		triggerRect,
		popoverRect.width,
		viewport,
		align
	);

	applyFixedPosition(element, nextTop, nextLeft, popoverRect);
}
